import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Table, ConfigProvider, Empty } from "antd";
import { PureCheckBoxAll, PureCheckBox } from "@/common/table";
import { useSelector, useDispatch } from "react-redux";
import {
  getTableScrollWidth,
  processColumnsForHoverMode,
} from "@/common/table-format-column-utils.jsx";
import get from "lodash.get";
import { Resizable } from "react-resizable";
import ReactDragListView from "react-drag-listview";
import {
  setColumns as setColumnsAction,
  selectCurrentDashboardFields,
} from "@/pages/dashboard/dashboard.slice";
import {
  getColumnStorageKey,
  applyPrefsToColumns,
  loadColumnPrefs,
  setColumnWidth,
  setColumnOrderFromColumns,
  reorderVisibleDashboardColumns,
} from "@/pages/dashboard/index.utils";
import {
  setSelectedTableItems as setSelectedTableItemsAction,
  setLastSelectedTableItem as setLastSelectedTableItemAction,
  setFilter as setFilterAction,
} from "@/pages/shared/shared.reducer";

const FIXED_DATA_INDEXES = ["random", "documentNumber", "fixed-hover-right"];

const ResizableTitle = (props) => {
  const { onResize, onResizeStop, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      axis="x"
      handle={
        <div
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      onResizeStop={onResizeStop}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const PureTable = (props) => {
  const {
    getTableHeight,
    currentPage,
    pageSize,
    tableData,
    identifier,
    tableView,
    id,
    columns,
    isResizable = false,
    rowKey,
    fetch,
    fetchAll,
    checkboxes,
    tableCount,
    isReorderable = false,
    virtual,
    nativeHorizontalScroll = virtual,
    customEmpty,
    rowClassName,
    expandable,
    getCheckboxProps,
    bodyWrapperClassName,
    formatColumnHoverMode,
  } = props;

  const { currentDashboard } = useSelector(selectCurrentDashboardFields);
  const reduxDispatch = useDispatch();

  const selectedItems = useSelector((state) =>
    get(state.shared, `${id}.selectedItems`)
  );
  const lastSelectedTableItem = useSelector((state) =>
    get(state.shared, `${id}.lastSelectedTableItem`)
  );
  const filters = useSelector((state) => get(state.shared, `${id}.filters`));
  const loading = useSelector(
    (state) => get(state.shared, `${id}.loading`) || false
  );

  const setFilter = useCallback(
    (filterId, value) => {
      reduxDispatch(setFilterAction(id || "documentDashboard", filterId, value));
    },
    [reduxDispatch, id]
  );

  const setSelectedTableItems = useCallback(
    (tableId, items, selectedRowKeys) => {
      reduxDispatch(setSelectedTableItemsAction(tableId, items, selectedRowKeys));
    },
    [reduxDispatch]
  );

  const setLastSelectedTableItem = useCallback(
    (tableId, item) => {
      reduxDispatch(setLastSelectedTableItemAction(tableId, item));
    },
    [reduxDispatch]
  );

  const commitColumns = useCallback(
    (value) => {
      const tableId = currentDashboard || id || "documentDashboard";
      reduxDispatch(setColumnsAction({ tableId, value }));
    },
    [reduxDispatch, currentDashboard, id]
  );

  const expandableProps = useMemo(() => {
    if (!expandable) return undefined;
    const { fixed, columnWidth, ...rest } = expandable || {};
    return {
      fixed: fixed ?? 'left',
      columnWidth: columnWidth ?? 36,
      ...rest,
    };
  }, [expandable]);

  const [tableHeight, setTableHeight] = useState(
    getTableHeight && getTableHeight()
  );
  const [hoverIndex, setHoverIndex] = useState(-1);
  const isScrolling = useRef(false);
  const tableRef = useRef(null);
  const horizontalSyncingRef = useRef(false);
  const previousPaginationRef = useRef({
    currentPage,
    pageSize,
  });

  useEffect(() => {
    const handleResize = () => {
      if (getTableHeight) {
        const nextHeight = getTableHeight();
        setTableHeight(nextHeight);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getTableHeight]);

  // Measure after layout: selection actions can grow or shrink the footer.
  const { sideView } = useSelector(selectCurrentDashboardFields);
  useEffect(() => {
    if (!getTableHeight) return;
    const frame = requestAnimationFrame(() => setTableHeight(getTableHeight()));
    return () => cancelAnimationFrame(frame);
  }, [sideView, getTableHeight, selectedItems?.length]);

  const scrollTableToTop = useCallback(() => {
    const tableInstance = tableRef.current;

    if (typeof tableInstance?.scrollTo === "function") {
      tableInstance.scrollTo({ top: 0 });
      return;
    }

    const tableElement = tableInstance?.nativeElement ?? tableInstance;
    if (!tableElement) {
      return;
    }

    const bodyScrollers = tableElement.querySelectorAll(
      ".ant-table-tbody-virtual-holder, .ant-table-body, .rc-virtual-list-holder"
    );

    bodyScrollers.forEach((scroller) => {
      if (typeof scroller.scrollTo === "function") {
        scroller.scrollTo({ top: 0 });
        return;
      }

      scroller.scrollTop = 0;
    });
  }, []);

  useEffect(() => {
    const previousPagination = previousPaginationRef.current;
    const paginationChanged =
      previousPagination.currentPage !== currentPage ||
      previousPagination.pageSize !== pageSize;

    if (paginationChanged) {
      scrollTableToTop();
    }

    previousPaginationRef.current = {
      currentPage,
      pageSize,
    };
  }, [currentPage, pageSize, scrollTableToTop]);

  const shouldUseLocalStorage =
    isReorderable &&
    (currentDashboard === "documentDashboard" ||
      currentDashboard === "allSitesDashboard");
  const storageKey = useMemo(
    () =>
      shouldUseLocalStorage
        ? getColumnStorageKey(currentDashboard, id, tableView)
        : "",
    [currentDashboard, id, shouldUseLocalStorage, tableView]
  );

  const [liveWidths, setLiveWidths] = useState({});

  const propsColumns = useMemo(
    () =>
      columns.map((col) =>
        liveWidths[col.dataIndex] != null
          ? { ...col, width: liveWidths[col.dataIndex] }
          : col
      ),
    [columns, liveWidths]
  );

  const toSerializableColumns = useCallback(
    (nextColumns) =>
      nextColumns.map((col) => ({
        key: col.key,
        dataIndex: col.dataIndex,
        width: col.width,
        isEnabled: col.isEnabled,
      })),
    []
  );

  const storedColumnPrefs = useMemo(
    () => loadColumnPrefs(columns.length ? storageKey : ""),
    // Resizing, reordering and resetting all commit new Redux columns after
    // updating storage. Re-read the overlay with that state change.
    [storageKey, columns]
  );

  const pendingResizeRef = useRef(null);

  const onResizeStop = useCallback(() => {
    const pending = pendingResizeRef.current;
    pendingResizeRef.current = null;
    if (!pending) return;

    const updated = propsColumns.map((col) =>
      col.dataIndex === pending.dataIndex
        ? { ...col, width: pending.width }
        : col
    );
    commitColumns(toSerializableColumns(updated));
    setLiveWidths((prev) => {
      if (prev[pending.dataIndex] == null) return prev;
      const next = { ...prev };
      delete next[pending.dataIndex];
      return next;
    });

    if (storageKey && !FIXED_DATA_INDEXES.includes(pending.dataIndex)) {
      setColumnWidth(storageKey, pending.dataIndex, pending.width);
    }
  }, [propsColumns, commitColumns, toSerializableColumns, storageKey]);

  const handleResize = useCallback(
    (index) =>
    (_, { size }) => {
      const minWidth = 40;
      if (size.width < minWidth) return;

      const visibleColumns = propsColumns.filter(
        (x) =>
          x.isEnabled ||
          x.dataIndex === "documentNumber" ||
          x.dataIndex === "fixed-hover-right"
      );
      const target = visibleColumns[index];
      if (!target) return;

      setLiveWidths((prev) => ({ ...prev, [target.dataIndex]: size.width }));

      pendingResizeRef.current = {
        dataIndex: target.dataIndex,
        width: size.width,
      };
    },
    [propsColumns]
  );

  const columns2 = useMemo(() => {
    const nextColumns = isResizable
    ? propsColumns
        .filter(
          (x) =>
            x.isEnabled ||
            x.dataIndex === "documentNumber" ||
            x.dataIndex === "fixed-hover-right"
        )
        .map((col, index) => ({
          ...col,
          onHeaderCell: (column) => ({
            width: column.width,
            onResize: handleResize(index),
            onResizeStop: () => onResizeStop(),
          }),
          onCell: () => ({ className: "ellipsis-column" }),
          width: col.width,
        }))
        .filter(
          (x) =>
            x.isEnabled ||
            x.dataIndex === "documentNumber" ||
            x.dataIndex === "fixed-hover-right"
        )
    : [...propsColumns];

    const hasCheckboxColumn = nextColumns.some(
      (column) => column.dataIndex === "random"
    );
    if (checkboxes && !hasCheckboxColumn) {
      nextColumns.unshift({
      title: () => {
        return (
          <div className="centered">
            <PureCheckBoxAll
              getCheckboxProps={getCheckboxProps}
              id={id}
              data={tableData}
              tableCount={tableCount}
              fetch={fetch}
              fetchAll={fetchAll}
            />
          </div>
        );
      },
      className: `checkbox-cell ${expandable ? 'has-expandable' : ''}`,
      width: 50,
      fixed: "left",
      dataIndex: "random",
      render: (_, item, index) => {
        const checkboxProps = getCheckboxProps
          ? getCheckboxProps(item)
          : {};
        return (
          <div className="selectionColumn">
            <PureCheckBox
              disabled={checkboxProps.disabled || false}
              id={id}
              index={index}
              data={tableData}
              item={item}
              rowKey={rowKey}
            />
          </div>
        );
      },
      });
    }

    return nextColumns;
  }, [
    isResizable,
    propsColumns,
    handleResize,
    onResizeStop,
    checkboxes,
    getCheckboxProps,
    id,
    tableData,
    tableCount,
    fetch,
    fetchAll,
    expandable,
    rowKey,
  ]);


  const onDragEnd = (fromIndex, toIndex) => {
    if (!storageKey || fromIndex === toIndex || toIndex < 2) return;
    const reorderedColumns = reorderVisibleDashboardColumns(propsColumns, fromIndex, toIndex);
    if (!reorderedColumns) return;
    setColumnOrderFromColumns(storageKey, reorderedColumns);
    commitColumns(toSerializableColumns(reorderedColumns));
  };

  const handleRowClick = useCallback(
    (e, record, rowIndex) => {
      const clickedCheckbox = e.target?.closest?.(
        ".selectionColumn, .ant-checkbox-wrapper, .ant-checkbox, .ant-checkbox-input"
      );

      if (clickedCheckbox) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const checkboxProps = getCheckboxProps && getCheckboxProps(record);
      const items = selectedItems ? [...selectedItems] : [];
      const checked = items.findIndex((x) => rowKey(x) === rowKey(record));

      if (e.nativeEvent.shiftKey && lastSelectedTableItem !== rowIndex) {
        const start = Math.min(rowIndex, lastSelectedTableItem);
        const finish = Math.max(rowIndex, lastSelectedTableItem);

        for (let i = start; i <= finish; i++) {
          const itemIndex = items.findIndex(
            (x) => rowKey(x) === rowKey(tableData[i])
          );
          if (itemIndex === -1) {
            items.push(tableData[i]);
          } else if (!checked && itemIndex !== -1) {
            items.splice(itemIndex, 1);
          }
        }
      } else {
        if (checked === -1 && !checkboxProps?.disabled) {
          items.push(record);
        } else if (checked !== -1) {
          items.splice(checked, 1);
        }
      }

      setSelectedTableItems(id, items);
      setLastSelectedTableItem(id, rowIndex);
    },
    [
      getCheckboxProps,
      id,
      lastSelectedTableItem,
      rowKey,
      selectedItems,
      setLastSelectedTableItem,
      setSelectedTableItems,
      tableData,
    ]
  );
  
  const computedUseHoverMode = useMemo(
    () => Boolean(formatColumnHoverMode),
    [formatColumnHoverMode]
  );
  const canSelectRows = Boolean(checkboxes);

  const processedColumns = useMemo(() => {
    const persistedColumns = shouldUseLocalStorage
      ? applyPrefsToColumns(columns2, storedColumnPrefs)
      : columns2;
    return processColumnsForHoverMode(
      persistedColumns,
      computedUseHoverMode,
      hoverIndex
    );
  }, [
    columns2,
    computedUseHoverMode,
    hoverIndex,
    shouldUseLocalStorage,
    storedColumnPrefs,
  ]);

  const reorderDecoration = (tableComponent) => {
    return isReorderable ? (
      <ReactDragListView.DragColumn
        onDragEnd={onDragEnd}
        nodeSelector="th"
        handleSelector=".dragHandler"
        ignoreSelector="react-resizable-handle"
      >
        {tableComponent()}
      </ReactDragListView.DragColumn>
    ) : (
      <div>{tableComponent()}</div>
    );
  };

  const getCompositeKey = (obj, identifier) => {
    const getNestedValue = (obj, path) =>
      path.split(".").reduce((acc, part) => acc && acc[part], obj);
    if (identifier) {
      if (Array.isArray(identifier)) {
        return identifier
          .map((singleIdentifier) => getNestedValue(obj, singleIdentifier))
          .join("-");
      }
      return getNestedValue(obj, identifier);
    }
  };

  const selectedRowKeys = useMemo(() => {
    if (!canSelectRows || !selectedItems || selectedItems.length === 0) {
      return [];
    }
    return selectedItems.map((item) => getCompositeKey(item, identifier));
  }, [canSelectRows, selectedItems, identifier]);

  const buildRowClassName = useCallback(
    (record, index, isSelected) => {
      return [
        typeof rowClassName === "function" ? rowClassName(record, index) : rowClassName,
        canSelectRows && isSelected ? "ant-table-row-selected" : null,
        index % 2 === 0 ? "table-row-light" : "table-row-dark",
      ]
        .filter(Boolean)
        .join(" ");
    },
    [canSelectRows, rowClassName]
  );

  const components = useMemo(
    () => ({
      header: {
        cell: ResizableTitle,
      },
    }),
    []
  );

  const calculateTotalWidth = useMemo(() => {
    return getTableScrollWidth(processedColumns, computedUseHoverMode);
  }, [processedColumns, computedUseHoverMode]);

  const scrollStopTimer = useRef(null);
  const handleScroll = useCallback(() => {
    if (!isScrolling.current) {
      isScrolling.current = true;
      setHoverIndex(-1);
      document.documentElement.classList.add('is-scrolling');
    }

    if (scrollStopTimer.current) {
      clearTimeout(scrollStopTimer.current);
    }
    scrollStopTimer.current = window.setTimeout(() => {
      isScrolling.current = false;
      document.documentElement.classList.remove('is-scrolling');
      scrollStopTimer.current = null;
    }, 150);
  }, []);

  useEffect(() => {
    if (!virtual || !nativeHorizontalScroll) {
      return undefined;
    }

    const tableRoot = tableRef.current?.nativeElement ?? tableRef.current;
    const nativeTable =
      tableRoot?.closest?.(".native-scroll-table") ?? tableRoot;
    const bodyHolder = nativeTable?.querySelector?.(
      ".ant-table-tbody-virtual-holder"
    );
    const header = nativeTable?.querySelector?.(".ant-table-header");

    if (!bodyHolder || !header) {
      return undefined;
    }

    const syncHorizontalScroll = (source) => {
      if (horizontalSyncingRef.current) {
        return;
      }

      horizontalSyncingRef.current = true;
      const maxBodyScrollLeft = Math.max(
        0,
        bodyHolder.scrollWidth - bodyHolder.clientWidth
      );
      const maxHeaderScrollLeft = Math.max(
        0,
        header.scrollWidth - header.clientWidth
      );
      const nextScrollLeft = Math.min(
        source.scrollLeft,
        maxBodyScrollLeft,
        maxHeaderScrollLeft
      );

      if (header.scrollLeft !== nextScrollLeft) {
        header.scrollLeft = nextScrollLeft;
      }
      if (bodyHolder.scrollLeft !== nextScrollLeft) {
        bodyHolder.scrollLeft = nextScrollLeft;
      }
      horizontalSyncingRef.current = false;
    };

    const handleBodyScroll = () => syncHorizontalScroll(bodyHolder);
    const handleHeaderScroll = () => syncHorizontalScroll(header);

    bodyHolder.addEventListener("scroll", handleBodyScroll, { passive: true });
    header.addEventListener("scroll", handleHeaderScroll, { passive: true });
    syncHorizontalScroll(bodyHolder);

    return () => {
      bodyHolder.removeEventListener("scroll", handleBodyScroll);
      header.removeEventListener("scroll", handleHeaderScroll);
    };
  }, [columns, nativeHorizontalScroll, tableData, virtual]);

  const getRowClassName = useCallback(
    (record, index) => {
      const compositeKey = getCompositeKey(record, identifier);
      const isSelected = selectedRowKeys.includes(compositeKey);
      return buildRowClassName(record, index, isSelected);
    },
    [selectedRowKeys, identifier, buildRowClassName]
  );

  const tableWrapperClassName = [
    "ant-table",
    virtual ? "virtual-table-scroll-optimized" : null,
    nativeHorizontalScroll ? "native-scroll-table" : null,
    nativeHorizontalScroll && Number.isFinite(tableHeight)
      ? "native-scroll-height"
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={tableWrapperClassName}
      style={{
        width: "100%",
        height: "100%",
        touchAction: "pan-x pan-y",
        ...(nativeHorizontalScroll && Number.isFinite(tableHeight)
          ? { "--dms-native-scroll-height": `${tableHeight}px` }
          : {}),
        ...(nativeHorizontalScroll && Number.isFinite(calculateTotalWidth)
          ? { "--dms-native-scroll-width": `${calculateTotalWidth}px` }
          : {}),
      }}
    >
      {reorderDecoration(() => (
        <ConfigProvider
          theme={{
            components: {
              Table: {
                height: "100%",
                rowHoverBg: canSelectRows ? "var(--dms-color-table-selected)" : "transparent",
                rowSelectedBg: canSelectRows ? "var(--dms-color-table-selected)" : "transparent",
                rowSelectedHoverBg: canSelectRows ? "var(--dms-color-table-selected)" : "transparent",
                borderColor: "var(--dms-color-border-strong)",
              },
            },
          }}
        >
          <Table
            ref={tableRef}
            className={props.expandable ? "has-expand" : ""}
            bordered
            id={id}
            expandable={expandableProps}
            columns={processedColumns}
            components={components}
            dataSource={tableData}
            locale={{
              emptyText: customEmpty || <Empty style={{ height: "calc(100vh - 342px)"}} />,
            }}
            rowKey={(record) => getCompositeKey(record, identifier)}
            rowClassName={getRowClassName}
            scroll={{
              y: tableHeight,
              x: calculateTotalWidth,
              scrollToFirstRowOnChange: true,
            }}
            style={{ width: "100%", height: "100%" }}
            onRow={(record, rowIndex) => ({
              onMouseEnter: computedUseHoverMode
                ? () => !isScrolling.current && setHoverIndex(rowIndex)
                : undefined,
              onMouseLeave: computedUseHoverMode
                ? () => !isScrolling.current && setHoverIndex(-1)
                : undefined,
              onTouchStart: computedUseHoverMode
                ? () => {
                    if (!isScrolling.current) setHoverIndex(rowIndex);
                  }
                : undefined,
              onClick: canSelectRows
                ? (e) => {
                    if (computedUseHoverMode && !isScrolling.current) {
                      setHoverIndex(rowIndex);
                    }
                    handleRowClick(e, record, rowIndex);
                  }
                : undefined,
            })}
            onChange={(pagination, tableFilters, sorter) => {
              const sortBy = sorter?.field;
              if (!sortBy) {
                return;
              }

              const sortDirection =
                sorter?.order === "descend" ? "desc" : "asc";

              if (filters?.sortBy !== sortBy) {
                setFilter("sortBy", sortBy);
              }

              if (filters?.sortDirection !== sortDirection) {
                setFilter("sortDirection", sortDirection);
              }
            }}
            onScroll={handleScroll}
            pagination={false}
            loading={props.tableLoading || loading}
            selectedRowKeys={selectedRowKeys}
            size="small"
            virtual={virtual}
            bodyWrapperClassName={bodyWrapperClassName}
          />
        </ConfigProvider>
      ))}
    </div>
  );
};

export default PureTable;
